export enum AnalyticsRankingMetric {
    Streams = 0,
    MinutesStreamed = 1,
    Views = 2,
    MinutesViewed = 3
}

export interface DailyUserSignupsResult {
    from: Date;
    to: Date;
    items: DailySignupCount[];
}

export interface DailySignupCount {
    date: Date;
    count: number;
}

export interface StreamingSummaryStats {
    streamCountAllTime: number;
    streamCountLast30Days: number;
    streamCountLast7Days: number;
    streamMinutesAllTime: number;
    streamMinutesLast30Days: number;
    streamMinutesLast7Days: number;
    watchMinutesAllTime: number;
    watchMinutesLast30Days: number;
    watchMinutesLast7Days: number;
}

export interface TotalUserSignupsCount {
    from: Date;
    to: Date;
    count: number;
}

export interface StreamsOverTimeResult {
    from: Date;
    to: Date;
    groupByHour: boolean;
    items: StreamsOverTimeItem[];
}

export interface StreamsOverTimeItem {
    dateDay: Date
    dateHour?: Date;
    titleId: string;
    titleName: string;
    userId: string;
    streams: number;
}

export interface StreamedMinutesOverTimeResult {
    from: Date;
    to: Date;
    groupByHour: boolean;
    items: StreamedMinutesOverTimeItem[];
}

export interface StreamedMinutesOverTimeItem {
    dateDay: Date;
    dateHour?: Date;
    titleId: string;
    titleName: string;
    userId: string;
    totalMinutesStreamed: number;
}

export interface MinutesViewedOverTimeResult {
    from: Date;
    to: Date;
    groupByHour: boolean;
    items: MinutesViewedOverTimeItem[];
}

export interface MinutesViewedOverTimeItem {
    dateDay: Date;
    dateHour?: Date;
    titleId: string;
    titleName: string;
    userId: string;
    totalMinutesViewed: number;
}

export interface ViewsOverTimeResult {
    from: Date;
    to: Date;
    groupByHour: boolean;
    items: ViewsOverTimeItem[];
}

export interface ViewsOverTimeItem {
    dateDay: Date;
    dateHour?: Date;
    platform: string;
    titleId: string;
    titleName: string;
    userId: string;
    totalViews: number;
}

export interface AverageViewsPerStreamResult {
    from: Date;
    to: Date;
    groupByHour: boolean;
    items: AverageViewsPerStreamItem[];
}

export interface AverageViewsPerStreamItem {
    dateDay: Date;
    dateHour?: Date;
    platform: string;
    titleId: string;
    titleName: string;
    userId: string;
    avgViewsPerStream: number;
}

export interface RankingsResult {
    from: Date;
    to: Date;
    metric: string;
    by: string;
    limit: number;
    platform: string;
    items: RankingsItem[];
}

export interface RankingsItem {
    keyId: string;
    keyName: string;
    value: number;
}

export interface ExperienceAnalyticsSummary {
    experienceId: string;
    name: string;
    shortDescription: string | null;
    companyId: string;
    streamingSessionsCountInLast30Days: number;
    streamingSessionsCountInLast60Days: number;
    streamingSessionsCountInLast90Days: number;
    streamingSessionsCountInLast7Days: number;
    streamingSessionsCountInLast24Hours: number;
    streamingSessionsCountInLast1Hour: number;
    localRecordingsCountInLast30Days: number;
    localRecordingsCountInLast60Days: number;
    localRecordingsCountInLast90Days: number;
    localRecordingsCountInLast7Days: number;
    localRecordingsCountInLast24Hours: number;
    localRecordingsCountInLast1Hour: number;
}

export interface AggregatedAnalyticsSummary {
    streamingSessionsCountInLast30Days: number;
    streamingSessionsCountInLast60Days: number;
    streamingSessionsCountInLast90Days: number;
    streamingSessionsCountInLast7Days: number;
    streamingSessionsCountInLast24Hours: number;
    streamingSessionsCountInLast1Hour: number;
    localRecordingsCountInLast30Days: number;
    localRecordingsCountInLast60Days: number;
    localRecordingsCountInLast90Days: number;
    localRecordingsCountInLast7Days: number;
    localRecordingsCountInLast24Hours: number;
    localRecordingsCountInLast1Hour: number;
}

export interface ExperienceAnalyticsLocalRecordingsByHour {
    count: number;
    averageDuration: number;
    date: Date;
}

export interface ExperienceAnalyticsSessionsByHour {
    count: number;
    averageDuration: number;
    date: Date;
}

export interface ExperienceAnalyticsForDay {
    localRecordings: ExperienceAnalyticsLocalRecordingsByHour[];
    sessions: ExperienceAnalyticsSessionsByHour[];
}

export interface ExperienceAnalyticsForDaySummary {
    totalLocalRecordings: number;
    totalSessions: number;
    averageLocalRecordingDuration: number;
    averageSessionDuration: number;
}

export interface DailyStreams {
    date: Date;
    streamCount: number;
    totalStreamMinutes: number;
    totalWatchMinutes: number;
}

export interface SumTitleStreams {
    titleId: string;
    titleName: string;
    streamCount: number;
    totalStreamMinutes: number;
    totalWatchMinutes: number;
}

export interface SumUserStreams {
    userId: string;
    userName: string;
    streamCount: number;
    totalStreamMinutes: number;
    totalWatchMinutes: number;
}

export interface DailyLiveStreamStats {
    dailyStreams: DailyStreams[];
    sumTitleStreams: SumTitleStreams[];
}

export interface DailyLiveStreamStatsByTitle {
    dailyStreams: DailyStreams[];
    sumStreams: SumTitleStreams;
    sumUsersStreams: SumUserStreams[];
}

export interface MinutesPerDay {
    date: Date;
    sessionCount: number;
    totalMinutes: number;
}

export interface EnterpriseStreamSummary {
    experienceId: string;
    displayName?: string;
    dataSessionCount: number;
    dataSessionMinutes: number;
    streamingSessionCount: number;
    streamingSessionMinutes: number;
}

export interface EnterpriseStreamDetail {
    experienceId: string;
    displayName?: string;
    totalDataSessionCount: number;
    totalDataSessionMinutes: number;
    totalStreamingSessionCount: number;
    totalStreamingSessionMinutes: number;
    dailyDataSessions: MinutesPerDay[];
    dailyStreamingSessions: MinutesPerDay[];
}

export interface DailySubscriptionAnalyticsResult {
    from: Date;
    to: Date;
    items: DailySubscriptionAnalyticsItem[];
}

export interface DailySubscriptionAnalyticsItem {
    date: Date;
    activeCount: number;
    trialCount: number;
    cancelledCount: number;
    activeCountChange: number;
    trialCountChange: number;
    cancelledCountChange: number;
}